import { ApiError } from "../api";
import { analytics } from "./index";
import { AnalyticsEvent } from "./events";

/**
 * Global safety net for JavaScript errors. Individual `try`/`catch` blocks around mutations
 * cover the failures we anticipated; this catches whatever they miss — a render crash outside
 * an error boundary, a stray promise rejection — so a silent white screen shows up here instead
 * of only as an unexplained support message.
 *
 * Called once from main.tsx, before anything else mounts.
 */
export function installGlobalErrorTracking(): void {
  window.addEventListener("error", (event) => {
    analytics.track(AnalyticsEvent.JsError, {
      message: event.message,
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason as unknown;
    analytics.track(AnalyticsEvent.JsError, {
      kind: "unhandled_rejection",
      message: reason instanceof Error ? reason.message : String(reason),
    });
  });
}

/**
 * Reports a failed API call without leaking request or response bodies — just the
 * machine-readable error code and HTTP status, which is enough to spot a spike in a particular
 * failure without capturing anything a user typed or was shown.
 *
 * `context` should name the action that failed (e.g. "submit_pick"), not the error itself.
 */
export function trackApiError(error: unknown, context: string): void {
  if (error instanceof ApiError) {
    analytics.track(AnalyticsEvent.ApiError, {
      context,
      code: error.code,
      status: error.status,
    });
    return;
  }
  analytics.track(AnalyticsEvent.JsError, {
    context,
    message: error instanceof Error ? error.message : String(error),
  });
}
